import React, {useState} from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Analytics as AnalyticsIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {analyzeFeedTrayImage, FeedTrayAnalysisResult} from "../../api/pond";

interface FeedTrayAnalysisProps {
  onAnalysisComplete?: (result: FeedTrayAnalysisResult) => void;
  initialImage?: string;
}

const FeedTrayAnalysis: React.FC<FeedTrayAnalysisProps> = ({onAnalysisComplete, initialImage}) => {
  const {t} = useTranslation();
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<FeedTrayAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      setResult(null);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) {
      setError("Please select an image file");
      return;
    }

    setAnalyzing(true);
    setError(null);
    console.log("Starting analysis for file:", selectedFile.name);

    try {
      const analysisResult = await analyzeFeedTrayImage(selectedFile);
      console.log("Analysis completed successfully:", analysisResult);
      setResult(analysisResult);
      onAnalysisComplete?.(analysisResult);
    } catch (err) {
      console.error("Analysis failed:", err);
      setError(err instanceof Error ? err.message : "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case "healthy":
        return "success";
      case "suspicious":
        return "warning";
      case "diseased":
        return "error";
      default:
        return "default";
    }
  };

  const getHealthIcon = (health: string) => {
    switch (health) {
      case "healthy":
        return <CheckCircleIcon />;
      case "suspicious":
        return <WarningIcon />;
      case "diseased":
        return <ErrorIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "high":
        return "error";
      case "medium":
        return "warning";
      case "low":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <Box>
      {/* File Upload Section */}
      <Card sx={{mb: 2}}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("Feed Tray Image Analysis")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
            {t("Upload a feed tray image with scale to analyze shrimp weight and detect diseases")}
          </Typography>

          <Box sx={{display: "flex", gap: 2, alignItems: "center"}}>
            <input accept="image/*" style={{display: "none"}} id="feed-tray-image-upload" type="file" onChange={handleFileSelect} />
            <label htmlFor="feed-tray-image-upload">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />} disabled={analyzing}>
                {t("Select Image")}
              </Button>
            </label>

            {selectedFile && (
              <Typography variant="body2" color="text.secondary">
                {selectedFile.name}
              </Typography>
            )}

            <Button
              variant="contained"
              startIcon={analyzing ? <CircularProgress size={20} /> : <AnalyticsIcon />}
              onClick={handleAnalyze}
              disabled={!selectedFile || analyzing}>
              {analyzing ? t("Analyzing...") : t("Analyze")}
            </Button>
          </Box>

          {/* Analysis Status */}
          {analyzing && (
            <Box sx={{mt: 2, display: "flex", alignItems: "center", gap: 1}}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Analyzing image with AI... This may take a few moments.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      )}

      {/* Analysis Results */}
      {result && (
        <Grid container spacing={2}>
          {/* Weight Estimation */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("Weight Estimation")}
                </Typography>
                <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                  <Box sx={{display: "flex", justifyContent: "space-between"}}>
                    <Typography variant="body2">{t("Total Weight")}:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {result.weightEstimation.totalWeight.toFixed(2)}g
                    </Typography>
                  </Box>
                  <Box sx={{display: "flex", justifyContent: "space-between"}}>
                    <Typography variant="body2">{t("Average Weight")}:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {result.weightEstimation.averageWeight.toFixed(2)}g
                    </Typography>
                  </Box>
                  <Box sx={{display: "flex", justifyContent: "space-between"}}>
                    <Typography variant="body2">{t("Count")}:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {result.weightEstimation.count}
                    </Typography>
                  </Box>
                  {result.weightEstimation.shrimpLength && (
                    <Box sx={{display: "flex", justifyContent: "space-between"}}>
                      <Typography variant="body2">{t("Average Length")}:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {result.weightEstimation.shrimpLength.toFixed(1)}
                        {result.weightEstimation.shrimpLengthUnit || "cm"}
                      </Typography>
                    </Box>
                  )}
                  {result.weightEstimation.individualWeight && (
                    <Box sx={{display: "flex", justifyContent: "space-between"}}>
                      <Typography variant="body2">{t("Individual Weight")}:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {result.weightEstimation.individualWeight.toFixed(2)}g
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{display: "flex", justifyContent: "space-between"}}>
                    <Typography variant="body2">{t("Confidence")}:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {(result.weightEstimation.confidence * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Disease Detection */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 2}}>
                  <Typography variant="h6">{t("Disease Detection")}</Typography>
                  <Chip
                    icon={getHealthIcon(result.diseaseDetection.overallHealth)}
                    label={t(result.diseaseDetection.overallHealth)}
                    color={getHealthColor(result.diseaseDetection.overallHealth)}
                    size="small"
                  />
                </Box>

                {result.diseaseDetection.diseases.length > 0 ? (
                  <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                    {result.diseaseDetection.diseases.map((disease, index) => (
                      <Box key={index} sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                        <Typography variant="body2" sx={{textTransform: "capitalize"}}>
                          {disease.name.replace("_", " ")}
                        </Typography>
                        <Box sx={{display: "flex", gap: 1, alignItems: "center"}}>
                          <Chip label={disease.severity} color={getSeverityColor(disease.severity)} size="small" />
                          <Typography variant="caption">{(disease.confidence * 100).toFixed(1)}%</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("No diseases detected")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Image Quality */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("Image Quality")}
                </Typography>
                <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                  <Box sx={{display: "flex", justifyContent: "space-between"}}>
                    <Typography variant="body2">{t("Brightness")}:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {(result.imageQuality.brightness * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                  <Box sx={{display: "flex", justifyContent: "space-between"}}>
                    <Typography variant="body2">{t("Contrast")}:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {(result.imageQuality.contrast * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                  <Box sx={{display: "flex", justifyContent: "space-between"}}>
                    <Typography variant="body2">{t("Blur")}:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {(result.imageQuality.blur * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Recommendations */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("Recommendations")}
                </Typography>
                <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                  {result.recommendations.map((recommendation, index) => (
                    <Typography key={index} variant="body2" color="text.secondary">
                      • {recommendation}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Details Dialog */}
      <Dialog open={showDetails} onClose={() => setShowDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t("Analysis Details")}</DialogTitle>
        <DialogContent>
          {result && (
            <Box sx={{mt: 1}}>
              <Typography variant="h6" gutterBottom>
                {t("Raw Analysis Data")}
              </Typography>
              <pre style={{fontSize: "12px", overflow: "auto"}}>{JSON.stringify(result, null, 2)}</pre>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetails(false)}>{t("Close")}</Button>
        </DialogActions>
      </Dialog>

      {/* View Details Button */}
      {result && (
        <Box sx={{mt: 2, display: "flex", justifyContent: "center"}}>
          <Button variant="outlined" onClick={() => setShowDetails(true)} startIcon={<InfoIcon />}>
            {t("View Analysis Details")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FeedTrayAnalysis;
